/**
 * `Field` (W1-12 ui-kit-i18n; contract FieldProps, DESIGN.md §4.11): the inspector row layout. Label column
 * (with an optional info popover and badges) next to the control; the Guided help line under the control
 * (hidden at the Full level by global.css, still available to screen readers through aria-describedby).
 * The row is a labelled group, so any control inside is announced with the field's label; the group is also
 * described by the explanations of its status badges (badges are not tab stops, see Badge.tsx).
 */

import { useId } from 'react';
import { BadgeList, hasStatusBadges } from './Badge';
import { useTr } from './bridge';
import s from './Field.module.css';
import { InfoPopover } from './InfoPopover';
import { cx } from './internal/cx';
import f from './internal/form.module.css';
import type { FieldProps } from './types';

export interface FieldExtraProps {
  readonly className?: string | undefined;
  /** Stack the label above the control (narrow panels, dialogs) instead of beside it. */
  readonly stacked?: boolean | undefined;
}

export function Field(props: FieldProps & FieldExtraProps) {
  const tr = useTr();
  const labelId = useId();
  const helpLineId = useId();
  const badgesId = useId();
  const help = props.help === undefined ? null : tr(props.help);
  const describedBy = [hasStatusBadges(props.badges) ? badgesId : null, help === null ? null : helpLineId]
    .filter((x) => x !== null)
    .join(' ');
  return (
    <div
      className={cx(s.field, props.stacked === true && s.stacked, props.className)}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      data-help-id={props.helpId}
    >
      <div className={s.labelCell}>
        <span id={labelId} className={cx(f.label, s.label)}>
          <span className={f.labelText}>{props.label}</span>
        </span>
        {props.info !== undefined && <InfoPopover {...props.info} />}
        <BadgeList badges={props.badges} descriptionId={badgesId} />
      </div>
      <div className={s.controlCell}>
        {props.children}
        {help !== null && (
          <p id={helpLineId} className={f.help} data-guided-help>
            {help}
          </p>
        )}
      </div>
    </div>
  );
}
